import React, { useState, useEffect } from "react";
import { Check, Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import api from "../../services/api";
import { useNavigate } from "react-router-dom";

export default function SaasPricing() {
  const [billingCycle, setBillingCycle] = useState("annual"); // monthly vs annual
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const res = await api.get("/saas/plans");
        setPlans(res.data || []);
      } catch (err) {
        console.error("Failed to fetch SaaS plans");
      } finally {
        setLoading(false);
      }
    };
    fetchPlans();
  }, []);

  const displayedPlans = plans.filter(p => p.billing_cycle === (billingCycle === "annual" ? "yearly" : "monthly"));

  return (
    <section id="pricing" className="py-24 bg-slate-50 dark:bg-slate-900 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-slate-900 dark:text-white tracking-tight">
            Predictive, Transparent Pricing
          </h2>
          <p className="mt-4 text-lg text-slate-600 dark:text-slate-300 font-medium">
            Scale your digital campus with zero hidden charges. Start with a 14-day free trial, no credit card required.
          </p>

          {/* Toggle */}
          <div className="flex justify-center items-center gap-3 mt-10">
            <span className={`text-sm font-bold ${billingCycle === "monthly" ? "text-slate-900 dark:text-white" : "text-slate-400"}`}>
              Monthly
            </span>
            <button
              onClick={() => setBillingCycle(billingCycle === "monthly" ? "annual" : "monthly")}
              className="w-12 h-6 bg-slate-200 dark:bg-slate-800 rounded-full p-1 relative flex items-center"
            >
              <motion.div
                layout
                className="w-4 h-4 bg-violet-600 rounded-full"
                animate={{ x: billingCycle === "monthly" ? 0 : 24 }}
                transition={{ type: "spring", stiffness: 500, damping: 30 }}
              />
            </button>
            <span className={`text-sm font-bold flex items-center gap-1.5 ${billingCycle === "annual" ? "text-slate-900 dark:text-white" : "text-slate-400"}`}>
              <span>Annually</span>
              <span className="px-2 py-0.5 bg-emerald-100 dark:bg-emerald-950 text-emerald-600 dark:text-emerald-400 text-[10px] font-black rounded-full uppercase">
                Save 20%
              </span>
            </span>
          </div>
        </div>

        {/* Pricing Cards */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="animate-spin text-violet-500 w-10 h-10" />
          </div>
        ) : displayedPlans.length === 0 ? (
          <div className="text-center py-20 text-slate-500">
            No {billingCycle === "annual" ? "yearly" : "monthly"} plans available at the moment.
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-8 max-w-6xl mx-auto items-stretch">
          {displayedPlans.map((plan) => {
            const isCustom = plan.price === 0 || !plan.price;
            const currentPrice = plan.price.toLocaleString();
            
            return (
              <div
                key={plan.id}
                className={`bg-white dark:bg-slate-950 rounded-[2.5rem] p-8 border flex flex-col justify-between relative transition-all duration-300 ${
                  plan.is_popular
                    ? "border-violet-500 shadow-2xl scale-[1.03] z-10"
                    : "border-slate-100 dark:border-slate-800 hover:shadow-xl"
                }`}
              >
                {/* Popular Ribbon */}
                {plan.is_popular && (
                  <div className="absolute top-0 right-8 -translate-y-1/2 px-4 py-1 bg-gradient-to-r from-violet-600 to-blue-650 text-white text-[10px] font-black rounded-full uppercase shadow-md">
                    Most Popular
                  </div>
                )}

                <div>
                  <h3 className="text-xl font-black text-slate-900 dark:text-white mb-2">{plan.name}</h3>
                  <p className="text-xs text-slate-500 dark:text-slate-450 leading-relaxed min-h-[50px] font-semibold">
                    {plan.description || "Perfect for educational campuses looking to manage online studies."}
                  </p>
                  
                  {/* Price */}
                  <div className="my-8 flex items-baseline gap-1 text-slate-900 dark:text-white">
                    <span className="text-4xl font-black">{isCustom ? "" : "₹"}</span>
                    <span className="text-5xl font-black tracking-tight">{isCustom ? "Custom" : currentPrice}</span>
                    <span className="text-xs text-slate-400 font-bold ml-1">{isCustom ? "" : `/ ${billingCycle === 'annual' ? 'year' : 'month'}`}</span>
                  </div>

                  {/* Feature Checklist */}
                  <div className="space-y-3.5 border-t border-slate-100 dark:border-slate-800 pt-6">
                    <div className="flex gap-2">
                      <Check size={16} className="text-violet-500 shrink-0 mt-0.5" />
                      <span className="text-xs font-bold text-slate-700 dark:text-slate-300 leading-snug">
                        {isCustom ? "Unlimited students & bandwidth" : `Up to ${plan.max_students.toLocaleString()} active students`}
                      </span>
                    </div>
                    {plan.features?.map((feat) => (
                      <div key={feat} className="flex gap-2">
                        <Check size={16} className="text-violet-500 shrink-0 mt-0.5" />
                        <span className="text-xs font-bold text-slate-700 dark:text-slate-300 leading-snug">{feat}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* CTA Button */}
                <button
                  onClick={() => navigate("/register-institute")}
                  className={`w-full mt-10 py-4 rounded-2xl text-xs font-black transition-all ${
                    plan.is_popular
                      ? "bg-gradient-to-r from-violet-600 to-blue-600 text-white shadow-xl shadow-blue-500/20 hover:scale-[1.03]"
                      : "bg-slate-50 hover:bg-slate-100 dark:bg-slate-900 dark:hover:bg-slate-850 text-slate-800 dark:text-white border border-slate-100 dark:border-slate-800"
                  }`}
                >
                  {isCustom ? "Contact Enterprise" : plan.price === 0 ? "Get Started" : "Purchase Plan"}
                </button>

              </div>
            );
          })}
        </div>
        )}

      </div>
    </section>
  );
}
